import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotification } from '../contexts/NotificationContext';
import './SupportPage.css';

interface FormData {
  name: string;
  email: string;
  issueType: string;
  title: string;
  content: string;
  images: File[];
}

const SupportPage: React.FC = () => {
  const { language } = useLanguage();
  const { showNotification } = useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    issueType: '',
    title: '',
    content: '',
    images: []
  });

  const content = {
    en: {
      title: 'Support & Feedback',
      subtitle: 'Report issues or send feedback to help us improve Lumina',
      nameLabel: 'Name / Discord Username',
      namePlaceholder: 'Your name or Discord username',
      emailLabel: 'Email Address',
      emailPlaceholder: 'your.email@example.com',
      issueTypeLabel: 'Issue Type',
      issuePlaceholder: 'Select issue type',
      titleLabel: 'Title',
      titlePlaceholder: 'Brief description of your issue',
      contentLabel: 'Description',
      contentPlaceholder: 'Please provide detailed information about your issue or feedback...',
      imagesLabel: 'Images (Optional)',
      imagesHelper: 'Upload up to 2 images (max 8MB each)',
      submitButton: 'Send Report',
      submittingButton: 'Sending...',
      issueTypes: {
        bug: 'Bug Report',
        feature: 'Feature Request',
        feedback: 'General Feedback',
        technical: 'Technical Support',
        other: 'Other'
      },
      validation: {
        nameRequired: 'Name or Discord username is required',
        emailRequired: 'Email address is required',
        emailInvalid: 'Please enter a valid email address',
        issueTypeRequired: 'Please select an issue type',
        titleRequired: 'Title is required',
        contentRequired: 'Description is required',
        imageTooLarge: 'Image size must be less than 8MB',
        tooManyImages: 'You can upload maximum 2 images',
        invalidImageType: 'Please upload only image files (JPG, PNG, GIF, WebP)'
      },
      success: 'Report sent successfully! We\'ll get back to you soon.',
      error: 'Failed to send report. Please try again later.'
    },
    ja: {
      title: 'サポート & フィードバック',
      subtitle: 'Luminaの改善にご協力ください。問題の報告やフィードバックをお送りください',
      nameLabel: '名前 / Discord ユーザー名',
      namePlaceholder: 'お名前またはDiscordユーザー名',
      emailLabel: 'メールアドレス',
      emailPlaceholder: 'your.email@example.com',
      issueTypeLabel: '問題の種類',
      issuePlaceholder: '問題の種類を選択',
      titleLabel: 'タイトル',
      titlePlaceholder: '問題の簡潔な説明',
      contentLabel: '詳細',
      contentPlaceholder: '問題やフィードバックの詳細をご記入ください...',
      imagesLabel: '画像（オプション）',
      imagesHelper: '最大2枚の画像をアップロード（各8MB以下）',
      submitButton: 'レポートを送信',
      submittingButton: '送信中...',
      issueTypes: {
        bug: 'バグレポート',
        feature: '機能リクエスト',
        feedback: '一般的なフィードバック',
        technical: 'テクニカルサポート',
        other: 'その他'
      },
      validation: {
        nameRequired: '名前またはDiscordユーザー名が必要です',
        emailRequired: 'メールアドレスが必要です',
        emailInvalid: '有効なメールアドレスを入力してください',
        issueTypeRequired: '問題の種類を選択してください',
        titleRequired: 'タイトルが必要です',
        contentRequired: '詳細が必要です',
        imageTooLarge: '画像サイズは8MB以下である必要があります',
        tooManyImages: '最大2枚の画像をアップロードできます',
        invalidImageType: '画像ファイルのみアップロードしてください（JPG、PNG、GIF、WebP）'
      },
      success: 'レポートが正常に送信されました！すぐにご連絡いたします。',
      error: 'レポートの送信に失敗しました。後でもう一度お試しください。'
    }
  };

  const t = content[language];

  const validateForm = (): string | null => {
    if (!formData.name.trim()) return t.validation.nameRequired;
    if (!formData.email.trim()) return t.validation.emailRequired;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return t.validation.emailInvalid;
    if (!formData.issueType) return t.validation.issueTypeRequired;
    if (!formData.title.trim()) return t.validation.titleRequired;
    if (!formData.content.trim()) return t.validation.contentRequired;
    return null;
  };

  const isFormValid = (): boolean => {
    return (
      formData.name.trim() !== '' &&
      formData.email.trim() !== '' &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
      formData.issueType !== '' &&
      formData.title.trim() !== '' &&
      formData.content.trim() !== ''
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    if (files.length > 2) {
      showNotification('error', t.validation.tooManyImages);
      e.target.value = '';
      return;
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 8 * 1024 * 1024;

    for (const file of files) {
      if (!validTypes.includes(file.type)) {
        showNotification('error', t.validation.invalidImageType);
        e.target.value = '';
        return;
      }

      if (file.size > maxSize) {
        showNotification('error', t.validation.imageTooLarge);
        e.target.value = '';
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      images: files
    }));
  };

  const removeImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));

    // Clear the file input to allow re-uploading the same file
    const fileInput = document.getElementById('images') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const sendToDiscordWebhook = async (data: FormData): Promise<boolean> => {
    try {
      const webhookUrl = process.env.REACT_APP_DISCORD_WEBHOOK_URL;

      if (!webhookUrl) {
        console.warn('Discord webhook URL not configured');
        await new Promise(resolve => setTimeout(resolve, 1000));
        return true;
      }

      const formData = new FormData();

      const embedPayload = {
        username: 'Lumina Support',
        avatar_url: `${window.location.origin}/assets/lumina/lumina.png`,
        embeds: [{
          title: `${data.issueType}: ${data.title}`,
          color: data.issueType === 'bug' ? 0xff6b6b : 0x6d8eff,
          fields: [
            {
              name: 'Reporter',
              value: data.name,
              inline: true
            },
            {
              name: 'Email',
              value: data.email,
              inline: true
            },
            {
              name: 'Issue Type',
              value: data.issueType,
              inline: true
            },
            {
              name: 'Description',
              value: data.content
            }
          ],
          timestamp: new Date().toISOString(),
          footer: {
            text: `Lumina Support System${data.images.length > 0 ? ` • ${data.images.length} image(s) attached` : ''}`
          }
        }]
      };

      formData.append('payload_json', JSON.stringify(embedPayload));

      if (data.images.length > 0) {
        data.images.forEach((file, index) => {
          formData.append(`file${index}`, file, file.name);
        });
      }

      const response = await fetch(webhookUrl, {
        method: 'POST',
        body: formData
      });

      return response.ok;
    } catch (error) {
      console.error('Error sending to Discord webhook:', error);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      showNotification('error', validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      const success = await sendToDiscordWebhook(formData);

      if (success) {
        showNotification('success', t.success);
        setFormData({
          name: '',
          email: '',
          issueType: '',
          title: '',
          content: '',
          images: []
        });

        const fileInput = document.getElementById('images') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      } else {
        showNotification('error', t.error);
      }
    } catch (error) {
      showNotification('error', t.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="support-page animate-fade">
      <div className="container">
        <section className="support-hero">
          <h1 className="support-title">{t.title}</h1>
          <p className="support-subtitle">{t.subtitle}</p>
        </section>

        <section className="support-form-section">
          <form className="support-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  {t.nameLabel}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t.namePlaceholder}
                  className="form-input"
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  {t.emailLabel} <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t.emailPlaceholder}
                  className="form-input"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="issueType" className="form-label">
                  {t.issueTypeLabel} <span className="required">*</span>
                </label>
                <select
                  id="issueType"
                  name="issueType"
                  value={formData.issueType}
                  onChange={handleInputChange}
                  className="form-select"
                  required
                  disabled={isSubmitting}
                >
                  <option value="">{t.issuePlaceholder}</option>
                  <option value="bug">{t.issueTypes.bug}</option>
                  <option value="feature">{t.issueTypes.feature}</option>
                  <option value="feedback">{t.issueTypes.feedback}</option>
                  <option value="technical">{t.issueTypes.technical}</option>
                  <option value="other">{t.issueTypes.other}</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="title" className="form-label">
                  {t.titleLabel} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder={t.titlePlaceholder}
                  className="form-input"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="content" className="form-label">
                {t.contentLabel} <span className="required">*</span>
              </label>
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                placeholder={t.contentPlaceholder}
                className="form-textarea"
                rows={6}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="images" className="form-label">
                {t.imagesLabel}
              </label>
              <div className="image-upload-container">
                <input
                  type="file"
                  id="images"
                  name="images"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="image-input"
                  disabled={isSubmitting}
                />
                <label htmlFor="images" className="image-upload-button">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z" fill="currentColor"/>
                  </svg>
                  Choose Images
                </label>
                <span className="image-upload-helper">{t.imagesHelper}</span>
              </div>

              {formData.images.length > 0 && (
                <div className="image-preview-container">
                  {formData.images.map((file, index) => (
                    <div key={index} className="image-preview-item">
                      <div className="image-preview-info">
                        <span className="image-name">{file.name}</span>
                        <span className="image-size">({(file.size / 1024 / 1024).toFixed(2)}MB)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="image-remove-button"
                        disabled={isSubmitting}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-submit">
              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting || !isFormValid()}
              >
                {isSubmitting ? t.submittingButton : t.submitButton}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default SupportPage;